/**
 * The circle tree as the chrome sees it, and the view's layout of it.
 *
 * `packages/shared/src/rings.ts` turns tags plus a `ViewLayout` into ring
 * geometry; nothing here knows about geometry. This is the flat list of `Tag`
 * rows read as a tree, the moves a keyboard can make on it, and the small
 * controller that holds the arrangement you are working in next to the one the
 * view holds: the scene hears about a change at once, and the server hears
 * about it when you say so.
 *
 * Two things live in different places on purpose (docs/CIRCLES.md §6):
 * **visibility and placement belong to the view**, so the same circles can be
 * arranged two ways; **name, colour and prominence belong to the circle**, so
 * they are the same in every view. The first half is a snapshot you save; the
 * second is data, and saves itself the moment it changes.
 */
import {
  ROOT_RING_ID,
  descendantIds,
  subtreeHeight as levelsBelow,
  type ChildPlacement,
  type Tag,
  type View,
  type ViewCamera,
  type ViewLayout,
  type ViewNode,
} from '@circle/shared';

export { ROOT_RING_ID };

/** The root is implicit and has no row; it is "Everyone" everywhere it is shown. */
export const ROOT_NAME = 'Everyone';

/** Tags nest three deep: a top-level circle is 1, its child 2, its child 3. */
export const MAX_DEPTH = 3;

/** Per level, as CIRCLES.md §4 has it. The slider works in logs of these. */
export const PROMINENCE_MIN = 0.25;
export const PROMINENCE_MAX = 4;

export const DEFAULT_NODE: ViewNode = { mode: 'merged', visible: true };

/** Steps on the prominence slider. Enough for a smooth drag, few enough to land on 1. */
export const PROMINENCE_STEPS = 100;

const LN_MIN = Math.log(PROMINENCE_MIN);
const LN_MAX = Math.log(PROMINENCE_MAX);

/**
 * Prominence is a multiplier, so the slider is logarithmic: half as loud and
 * twice as loud are the same distance from the middle, and the default sits
 * exactly there. `0.25 … 1 … 4` maps to `0 … 50 … 100`.
 */
export function prominenceToSlider(prominence: number): number {
  const clamped = Math.min(Math.max(prominence, PROMINENCE_MIN), PROMINENCE_MAX);
  return Math.round(((Math.log(clamped) - LN_MIN) / (LN_MAX - LN_MIN)) * PROMINENCE_STEPS);
}

/**
 * Where the slider is allowed to stop.
 *
 * A continuous log slider could name ×1.52 but never ×1.5: 100 steps over two
 * octaves land on numbers nobody meant, and the one weight anybody actually
 * wants above the default — half again as loud — was unreachable. These are the
 * numbers a person would say out loud, closer together around 1 where the
 * difference is small and easy to miss. Typing in the readout is the way past
 * them for anyone who really does want ×1.52.
 */
export const PROMINENCE_STOPS: readonly number[] = [
  0.25, 0.33, 0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4,
];

/**
 * The stop nearest `value`, measured in logs — prominence is a multiplier, so
 * ×0.5 and ×2 are the same distance from ×1 and should snap the same way.
 */
export function snapProminence(value: number): number {
  const clamped = Math.min(Math.max(value, PROMINENCE_MIN), PROMINENCE_MAX);
  const at = Math.log(clamped);
  let best = PROMINENCE_STOPS[0]!;
  let bestGap = Infinity;
  for (const stop of PROMINENCE_STOPS) {
    const gap = Math.abs(Math.log(stop) - at);
    if (gap < bestGap) {
      bestGap = gap;
      best = stop;
    }
  }
  return best;
}

export function prominenceFromSlider(value: number): number {
  const at = Math.min(Math.max(value, 0), PROMINENCE_STEPS) / PROMINENCE_STEPS;
  return snapProminence(Math.exp(LN_MIN + at * (LN_MAX - LN_MIN)));
}

/**
 * A weight somebody typed. `×1.5`, `1.5`, `1,5` and ` x1.5 ` all mean the same
 * thing; anything that is not a number at all is a refusal rather than a 1, so
 * the caller can put the old value back instead of quietly changing it. Typed
 * values are clamped but *not* snapped: typing is how you say a number the
 * stops do not offer.
 */
export function parseProminence(text: string): number | null {
  const cleaned = text.trim().replace(/^[×xX*]\s*/, '').replace(',', '.');
  if (!/^\d*\.?\d+$/.test(cleaned)) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value <= 0) return null;
  const clamped = Math.min(Math.max(value, PROMINENCE_MIN), PROMINENCE_MAX);
  // Two decimals, so what the readout says is what the server is sent.
  return Math.round(clamped * 100) / 100;
}

/** `×1.4`, `×1`, `×0.25` — trailing zeroes trimmed so 1 reads as 1. */
export function formatProminence(prominence: number): string {
  return `×${prominence.toFixed(2).replace(/\.?0+$/, '')}`;
}

/**
 * How a circle may sit on its parent (docs/CIRCLES.md §2), in the words the
 * panel uses. `hint` names the parent, so it is a function of where the row is.
 */
export const PLACEMENTS: {
  value: ChildPlacement;
  label: string;
  hint: (parent: string) => string;
}[] = [
  { value: 'band', label: 'Band', hint: (parent) => `A band on ${parent}` },
  { value: 'nested', label: 'Inside', hint: (parent) => `A ring inside ${parent}` },
  { value: 'orbit', label: 'Own ring', hint: (parent) => `A ring of its own, on an orbit round ${parent}` },
];

/** One node of the tree: the root carries no tag. */
export interface CircleNode {
  id: string;
  tag: Tag | undefined;
  /** 0 for the root, 1 for a top-level circle. */
  depth: number;
  parentId: string | null;
  children: CircleNode[];
}

function bySort(a: Tag, b: Tag): number {
  return a.sort - b.sort || a.name.localeCompare(b.name);
}

/**
 * The flat rows as a tree, rooted at "Everyone". A row whose `parentId` names
 * nothing in the list is treated as a top-level circle rather than dropped: a
 * half-loaded bundle should still draw something.
 */
export function buildCircleTree(tags: readonly Tag[]): CircleNode {
  const known = new Set(tags.map((t) => t.id));
  const root: CircleNode = { id: ROOT_RING_ID, tag: undefined, depth: 0, parentId: null, children: [] };
  const nodes = new Map<string, CircleNode>([[ROOT_RING_ID, root]]);
  for (const tag of tags) {
    nodes.set(tag.id, { id: tag.id, tag, depth: 0, parentId: null, children: [] });
  }
  for (const tag of [...tags].sort(bySort)) {
    const node = nodes.get(tag.id)!;
    const parentId = tag.parentId && known.has(tag.parentId) ? tag.parentId : null;
    const parent = nodes.get(parentId ?? ROOT_RING_ID)!;
    node.parentId = parent.id;
    node.depth = parent.depth + 1;
    parent.children.push(node);
  }
  // A cycle among the rows would leave nodes unreachable from the root; they
  // are simply not drawn, which is the same as the worker refusing to make one.
  return root;
}

/** The tree flattened back out, parents before children — the order it is drawn in. */
export function flattenTree(root: CircleNode): CircleNode[] {
  const out: CircleNode[] = [];
  const walk = (node: CircleNode): void => {
    out.push(node);
    for (const child of node.children) walk(child);
  };
  walk(root);
  return out;
}

/**
 * How many levels the subtree at `node` occupies. A leaf is 1 — one more than
 * the shared `subtreeHeight`, which counts the levels *below*, because the
 * depth check adds this to where the node is about to land.
 */
export function subtreeHeight(node: CircleNode): number {
  return 1 + levelsBelow(flattenTree(node), node.id);
}

export function findNode(root: CircleNode, id: string): CircleNode | undefined {
  return flattenTree(root).find((n) => n.id === id);
}

/** True while `maybeAncestor` is on the path from the root to `id`. */
export function isAncestor(root: CircleNode, maybeAncestor: string, id: string): boolean {
  return descendantIds(flattenTree(root), maybeAncestor).has(id);
}

// --- the moves a keyboard can make -------------------------------------------

/**
 * A move, as the patches it takes. Reordering within a parent renumbers the
 * whole sibling run rather than swapping two `sort` values: the numbers stay
 * dense, and two moves in a row cannot collide on one.
 */
export interface TagMove {
  id: string;
  patch: { parentId?: string | null; sort?: number };
}

function siblingsOf(root: CircleNode, parentId: string): CircleNode[] {
  return findNode(root, parentId)?.children ?? [];
}

function renumber(siblings: CircleNode[], parentId: string | null): TagMove[] {
  return siblings.map((node, index) => ({
    id: node.id,
    patch: { parentId: parentId === ROOT_RING_ID ? null : parentId, sort: index },
  }));
}

/** Swap a circle with the sibling above or below it. Empty when there is none. */
export function moveWithinParent(root: CircleNode, id: string, delta: -1 | 1): TagMove[] {
  const node = findNode(root, id);
  if (!node || !node.parentId) return [];
  const siblings = [...siblingsOf(root, node.parentId)];
  const at = siblings.findIndex((n) => n.id === id);
  const to = at + delta;
  if (at < 0 || to < 0 || to >= siblings.length) return [];
  [siblings[at], siblings[to]] = [siblings[to]!, siblings[at]!];
  return renumber(siblings, node.parentId);
}

/**
 * Make a circle a child of the sibling above it — the keyboard's answer to
 * dragging it onto that row. Refused when it would push the tree past
 * {@link MAX_DEPTH}, which is the check the worker makes too (`TAG_DEPTH`).
 */
export function indentUnderPrevious(root: CircleNode, id: string): TagMove[] {
  const node = findNode(root, id);
  if (!node || !node.parentId) return [];
  const siblings = siblingsOf(root, node.parentId);
  const at = siblings.findIndex((n) => n.id === id);
  const target = siblings[at - 1];
  if (!target) return [];
  if (target.depth + subtreeHeight(node) > MAX_DEPTH) return [];
  return [{ id, patch: { parentId: target.id, sort: target.children.length } }];
}

/** Lift a circle out to sit just after its parent. Refused at the top level. */
export function outdentToGrandparent(root: CircleNode, id: string): TagMove[] {
  const node = findNode(root, id);
  const parent = node?.parentId ? findNode(root, node.parentId) : undefined;
  if (!node || !parent || parent.id === ROOT_RING_ID) return [];
  const grandparentId = parent.parentId ?? ROOT_RING_ID;
  const siblings = [...siblingsOf(root, grandparentId)];
  const at = siblings.findIndex((n) => n.id === parent.id);
  siblings.splice(at + 1, 0, node);
  return renumber(siblings, grandparentId);
}

/**
 * Drop `id` onto `targetId`: as a child when `where` is `'into'`, as the
 * sibling after it when `'after'`. Refused for a move into the dragged circle's
 * own subtree (`TAG_CYCLE`) or past {@link MAX_DEPTH} (`TAG_DEPTH`).
 */
export function dropOn(
  root: CircleNode,
  id: string,
  targetId: string,
  where: 'into' | 'after',
): TagMove[] {
  const node = findNode(root, id);
  const target = findNode(root, targetId);
  if (!node || !target || node.id === target.id) return [];
  if (isAncestor(root, id, targetId)) return [];

  if (where === 'into') {
    if (target.depth + subtreeHeight(node) > MAX_DEPTH) return [];
    if (node.parentId === target.id) return [];
    return [{ id, patch: { parentId: target.id === ROOT_RING_ID ? null : target.id, sort: target.children.length } }];
  }

  const parentId = target.parentId ?? ROOT_RING_ID;
  if (findNode(root, parentId)!.depth + subtreeHeight(node) > MAX_DEPTH) return [];
  const siblings = [...siblingsOf(root, parentId)].filter((n) => n.id !== id);
  const at = siblings.findIndex((n) => n.id === targetId);
  siblings.splice(at + 1, 0, node);
  return renumber(siblings, parentId);
}

// --- the view's layout -------------------------------------------------------

export function nodeOf(layout: ViewLayout | undefined, id: string): ViewNode {
  return layout?.nodes?.[id] ?? DEFAULT_NODE;
}

/** A layout with one node changed. Never mutates the one it was given. */
export function withNode(layout: ViewLayout, id: string, patch: Partial<ViewNode>): ViewLayout {
  return { ...layout, nodes: { ...layout.nodes, [id]: { ...nodeOf(layout, id), ...patch } } };
}

/**
 * A layout that names every circle the calendar has.
 *
 * The scene is happy with gaps — an absent node takes the defaults — but a
 * *saved* view that names only what was touched would silently pick up circles
 * made after it, which is not what "this is how I like it" means.
 */
export function completeLayout(tags: readonly Tag[], layout: ViewLayout | undefined): ViewLayout {
  const nodes: Record<string, ViewNode> = { [ROOT_RING_ID]: { ...nodeOf(layout, ROOT_RING_ID) } };
  for (const tag of tags) nodes[tag.id] = { ...nodeOf(layout, tag.id) };
  return { nodes };
}

/** The default view, or the first one, or nothing at all. */
export function defaultView(views: readonly View[]): View | undefined {
  return views.find((v) => v.isDefault) ?? views[0];
}

// --- what the worker says when it says no ------------------------------------

const CIRCLE_ERRORS: Record<string, string> = {
  TAG_DEPTH: 'Circles go three deep. Move this one out a level first.',
  TAG_CYCLE: 'A circle cannot go inside itself.',
  TAG_EXISTS: 'There is already a circle with that name here.',
  TAG_FOREIGN: 'That circle belongs to another calendar.',
  VIEW_LAST: 'This is the only view. Make another one before removing it.',
};

/** The message for a circles-specific code, or nothing for anything else. */
export function circleErrorMessage(code: string | undefined): string | undefined {
  return code ? CIRCLE_ERRORS[code] : undefined;
}

// --- the working layout ------------------------------------------------------

export interface WorkingLayoutOptions {
  /** Hand the layout to the scene. Called on every change, at once. */
  apply: (layout: ViewLayout, animate: boolean) => void;
  /**
   * Where the camera is now, for `save` to record alongside the arrangement.
   * Absent for a viewer, and for the tests that only care about the layout.
   */
  camera?: () => ViewCamera;
  /** Put the camera back where the view left it, on revert. */
  applyCamera?: (camera: ViewCamera) => void;
  /**
   * Write it into the view it came from. Absent for a viewer, whose
   * arrangement is theirs alone and never leaves the browser.
   */
  save?: (viewId: string, layout: ViewLayout, camera: ViewCamera | undefined) => Promise<unknown>;
  onError?: (error: unknown) => void;
  /** The working layout started or stopped differing from the saved one. */
  onDirtyChange?: (dirty: boolean) => void;
}

function sameOverride(a: ViewNode['override'], b: ViewNode['override']): boolean {
  if (!a || !b) return !a === !b;
  return a.cx === b.cx && a.cy === b.cy && a.scale === b.scale;
}

/**
 * Two layouts are the same arrangement when they draw the same.
 *
 * Field by field rather than by `JSON.stringify`, and an absent node counts as
 * the default one — which is what the scene makes of it — so a layout that has
 * simply been completed over a new circle does not read as an edit.
 */
function sameLayout(a: ViewLayout, b: ViewLayout): boolean {
  const keys = new Set([...Object.keys(a.nodes ?? {}), ...Object.keys(b.nodes ?? {})]);
  for (const key of keys) {
    const x = a.nodes?.[key] ?? DEFAULT_NODE;
    const y = b.nodes?.[key] ?? DEFAULT_NODE;
    if (
      x.mode !== y.mode ||
      x.placement !== y.placement ||
      x.visible !== y.visible ||
      !sameOverride(x.override, y.override)
    ) {
      return false;
    }
  }
  return true;
}

/**
 * How far the camera may drift before it counts as having moved.
 *
 * A camera is read back off a float transform and written to the hash rounded,
 * so it never returns bit-identical to what was saved; without a tolerance
 * Revert would put the camera "back" to a place it never left. These are the
 * smallest differences anyone can see: `tx`/`ty` are screen pixels already (the
 * camera group is `translate(tx,ty) scale(scale)`), so two of them is a hair
 * either way at any zoom, and `scale` is relative because a 1% error is the
 * same size on screen wherever you are.
 */
export const CAMERA_EPSILON = {
  scale: 0.01,
  translate: 2,
  rotation: (0.5 * Math.PI) / 180,
} as const;

/** Whether two cameras are pointing at the same thing, within {@link CAMERA_EPSILON}. */
export function sameCamera(a: ViewCamera, b: ViewCamera): boolean {
  if (Math.abs(a.scale - b.scale) > CAMERA_EPSILON.scale * Math.max(Math.abs(b.scale), 1e-6)) {
    return false;
  }
  if (Math.abs(a.tx - b.tx) > CAMERA_EPSILON.translate) return false;
  if (Math.abs(a.ty - b.ty) > CAMERA_EPSILON.translate) return false;
  // Rotation is a circle: a hair clockwise of 0 and a hair anticlockwise are
  // the same place, and normalising is what says so.
  const turn = 2 * Math.PI;
  let d = Math.abs(a.rotation - b.rotation) % turn;
  if (d > turn / 2) d = turn - d;
  return d <= CAMERA_EPSILON.rotation;
}

/**
 * The arrangement you are working in, and the one the view holds.
 *
 * A view used to be rewritten by whatever you did next: every mode and
 * visibility change debounced itself into the selected view, so the default
 * everyone opens on drifted under its owner without anyone deciding it should.
 * A view is now a **snapshot**. Selecting one loads it into a working layout;
 * changing a mode changes the working layout and the scene at once and nothing
 * else; `save()` is the only thing that writes, and `revert()` throws the work
 * away.
 *
 * **The camera rides along; it is not an edit.** Save writes where you are
 * looking from, because a view is an arrangement *and* somewhere to look at it
 * from — but spinning the ring or zooming into March is looking, not
 * arranging, and it never lights the dot or raises the Save/Discard question.
 * `dirty` is the arrangement alone. Revert still puts the camera back to where
 * the view holds it, compared with {@link sameCamera} so a hair of rounding is
 * not a move.
 */
export class WorkingLayout {
  private saved: ViewLayout = { nodes: {} };
  private working: ViewLayout = { nodes: {} };
  private savedCamera: ViewCamera | undefined;
  private viewId: string | null = null;
  private announced = false;
  private gone = false;
  private readonly watchers = new Set<(layout: ViewLayout) => void>();

  constructor(private readonly options: WorkingLayoutOptions) {}

  /** What the scene is drawing. */
  get current(): ViewLayout {
    return this.working;
  }

  /** What the view holds, which is what `revert` goes back to. */
  get baseline(): ViewLayout {
    return this.saved;
  }

  /** The camera the view holds, if it kept one. `revert` goes back to it too. */
  get baselineCamera(): ViewCamera | undefined {
    return this.savedCamera;
  }

  get currentViewId(): string | null {
    return this.viewId;
  }

  /**
   * Unsaved work: the pill's dot, and the reason switching views asks first.
   * The arrangement only — where the camera is pointing is never unsaved work.
   */
  get dirty(): boolean {
    return !sameLayout(this.working, this.saved);
  }

  /** The camera has moved off the one the view holds, by more than rounding. */
  get cameraMoved(): boolean {
    const saved = this.savedCamera;
    const read = this.options.camera;
    if (!saved || !read) return false;
    return !sameCamera(read(), saved);
  }

  /**
   * Hear about the working layout whenever it changes, from wherever.
   *
   * The Circles panel draws the modes from it, and Revert and a view switch
   * both change it from outside the panel — without this the segmented control
   * would go on claiming the mode that had just been thrown away. Returns the
   * way to stop listening.
   */
  watch(fn: (layout: ViewLayout) => void): () => void {
    this.watchers.add(fn);
    return () => {
      this.watchers.delete(fn);
    };
  }

  /** Whether saving is possible at all — a viewer's sync cannot. */
  get savable(): boolean {
    return Boolean(this.options.save && this.viewId);
  }

  /**
   * Open a view: both layouts become its saved one, and nothing is dirty.
   *
   * `apply: false` takes the layout without handing it to the scene, for the
   * one caller that has already put it there — the page's opening `RingView`,
   * which needs the layout in place before it can work out where to point the
   * camera. Re-applying it would refit and throw away the camera it just chose.
   */
  adopt(
    viewId: string | null,
    layout: ViewLayout,
    opts: { animate?: boolean; apply?: boolean; camera?: ViewCamera } = {},
  ): void {
    this.viewId = viewId;
    this.saved = layout;
    this.working = layout;
    // The view's own camera, or none: it is what Revert goes back to.
    this.savedCamera = opts.camera;
    if (opts.apply !== false) this.options.apply(layout, opts.animate === true);
    this.announce();
  }

  /** An edit: the working layout and the scene, and nowhere else. */
  set(layout: ViewLayout, opts: { animate?: boolean } = {}): void {
    this.working = layout;
    this.options.apply(layout, opts.animate === true);
    this.announce();
  }

  /**
   * A circle was made or deleted, so both layouts have to name it.
   *
   * This is not an edit — the tree moved under the arrangement rather than the
   * arrangement moving — so the saved layout is refreshed alongside the working
   * one and a view that was clean stays clean.
   */
  rebase(saved: ViewLayout, working: ViewLayout, opts: { animate?: boolean } = {}): void {
    const changed = !sameLayout(working, this.working);
    this.saved = saved;
    this.working = working;
    if (changed) this.options.apply(working, opts.animate === true);
    this.announce();
  }

  /**
   * Write the working layout, and where you are looking from, into the view.
   * The camera goes silently: it was never unsaved work, but a view is also
   * somewhere to look from, and the place you pressed Save is the best guess.
   */
  async save(): Promise<boolean> {
    const save = this.options.save;
    const viewId = this.viewId;
    if (!save || !viewId) return false;
    const layout = this.working;
    const camera = this.options.camera?.();
    try {
      await save(viewId, layout, camera);
    } catch (error) {
      this.options.onError?.(error);
      return false;
    }
    // Not `this.working`, and not the camera as it is *now*: an edit made while
    // the request was in flight is still unsaved, and the dot should stay lit
    // for it. A view that had no camera has one from here on.
    this.saved = layout;
    this.savedCamera = camera;
    this.announce();
    return true;
  }

  /**
   * Throw the working layout away and go back to what the view holds — the
   * camera too, where the view kept one and it has moved since.
   */
  revert(opts: { animate?: boolean } = {}): void {
    if (!this.dirty) return;
    // Read before the layout goes back: applying it can refit and move the
    // camera by itself, and then there would be nothing left to put back.
    const camera = this.cameraMoved ? this.savedCamera : undefined;
    this.working = this.saved;
    this.options.apply(this.saved, opts.animate === true);
    if (camera) this.options.applyCamera?.(camera);
    this.announce();
  }

  destroy(): void {
    this.gone = true;
    this.watchers.clear();
  }

  private announce(): void {
    if (this.gone) return;
    for (const watcher of this.watchers) watcher(this.working);
    const dirty = this.dirty;
    if (dirty === this.announced) return;
    this.announced = dirty;
    this.options.onDirtyChange?.(dirty);
  }
}

/** The narrow slice of `window` {@link guardUnsaved} needs, so a test can be one. */
export interface UnloadTarget {
  addEventListener(type: 'beforeunload', listener: (event: BeforeUnloadEvent) => void): void;
  removeEventListener(type: 'beforeunload', listener: (event: BeforeUnloadEvent) => void): void;
}

/**
 * Ask before a reload or a closed tab throws unsaved work away.
 *
 * Switching views asks through `choiceDialog`, but leaving the page altogether
 * cannot: the browser's own dialog is the only one there is room for. It is
 * raised only where there is something to lose — `savable` is false for a
 * viewer, whose arrangement was never going anywhere, so they are never
 * stopped. Returns the way to take the guard back off.
 */
export function guardUnsaved(working: WorkingLayout, target: UnloadTarget = window): () => void {
  const listener = (event: BeforeUnloadEvent): void => {
    if (!working.savable || !working.dirty) return;
    event.preventDefault();
    // Chrome wants a truthy `returnValue` as well; no browser shows the text.
    event.returnValue = '';
  };
  target.addEventListener('beforeunload', listener);
  return () => target.removeEventListener('beforeunload', listener);
}
